import { useMemo } from "react";

import { ClockCircleOutlined, FilterOutlined } from "@ant-design/icons";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";
import { BlockContainer } from "Components/elements/RVFlow/Shared/FlowNodeBlocks/BlockContainer/BlockContainer";
import { BlockContent } from "Components/elements/RVFlow/Shared/FlowNodeBlocks/BlockContent/BlockContent";
import { BlockTitle } from "Components/elements/RVFlow/Shared/FlowNodeBlocks/BlockTitle/BlockTitle";
import sharedStyles from "Components/elements/RVFlow/Shared/FlowNodeBlocks/FlowNodeBlocks.module.scss";
import {
    getJourneyNodeContentSummary,
    getJourneyNodeIcon,
    getJourneyNodeTypeConfig,
    getMetadataFromTriggerFilterEvent,
    type ParsedEventFiltersMetadata,
} from "Components/elements/RVFlow/Shared/JourneyNodePresentation";
// todo figure out the fight place for this
import { NodeSubtext } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/Components/NodeSubtext/NodeSubtext";
import { NodeData, NodeTriggerType, NodeType } from "Types/Journey";
import { DynamicCondition } from "Types/Segment";

import { showContactConditions } from "../../../Components/JourneyActionsPanel/NodeEditorPanel/shared";
import { prettyPrintDate } from "../../helpers";

import styles from "./TriggerBlock.module.scss";

type TriggerData = NodeData & {
    eventName?: string;
    triggerType: NodeTriggerType;
    analyticsData: Record<string, any>;
    disableSimultaneousEntrances?: boolean;
    hiddenJourney?: boolean;
    lifetimeLimit?: number;
    scheduledJourneyStartDate?: Date;
    scheduledJourneyFrequency?: string;
    segmentId?: string;
    segmentName?: string;
    triggerFilters?: DynamicCondition[];
    triggerFiltersEvents?: DynamicCondition[];
};

// TODO: update type to import type { SharedBlockProps } from "Components/elements/RVFlow/RVFlowTypes";
// and follow up updateContactBlock or the IVR blocks work
const BLOCK_VARIANT = getJourneyNodeTypeConfig(NodeType.Trigger).variant;
const BLOCK_ICON = getJourneyNodeIcon(NodeType.Trigger);

export function TriggerBlock({ data }: { data: TriggerData }): JSX.Element {
    const triggerAttributes: ParsedEventFiltersMetadata = useMemo(() => {
        const dataCopy = data as any;
        return getMetadataFromTriggerFilterEvent(dataCopy);
    }, [data]);

    const isActive = triggerAttributes?.eventType || data?.empty;

    return (
        <BlockContainer variant={BLOCK_VARIANT} icon={BLOCK_ICON}>
            <BlockTitle nodeData={data} fallBackDisplay={"Trigger"}>
                {/* todo figure out this icon*/}
                {data?.hiddenJourney && (
                    <RVIcon icon="hidden" size={RVIconSizeOptions.SMALL} color="gray-3" className={styles.hiddenIcon} />
                )}
            </BlockTitle>

            <BlockContent isActive={!!isActive}>
                <div className={sharedStyles.breakText}>
                    {getJourneyNodeContentSummary(NodeType.Trigger, data as unknown as Record<string, unknown>)}
                </div>
                {triggerAttributes?.eventType === NodeTriggerType.Scheduled && (
                    <NodeSubtext
                        icon={<ClockCircleOutlined />}
                        text={prettyPrintDate(data.scheduledJourneyStartDate)}
                    />
                )}
                {triggerAttributes && triggerAttributes.filtersCount > 0 && (
                    <NodeSubtext
                        icon={<FilterOutlined />}
                        text={`${triggerAttributes?.filtersCount} Trigger Event Condition${
                            triggerAttributes.filtersCount > 1 ? "s" : ""
                        }`}
                    />
                )}
                {showContactConditions(triggerAttributes?.eventType as NodeTriggerType) && data?.triggerFilters && (
                    <NodeSubtext
                        icon={<FilterOutlined />}
                        text={`${data.triggerFilters.length} Contact Attribute Condition${
                            data.triggerFilters.length > 1 ? "s" : ""
                        }`}
                    />
                )}
            </BlockContent>
        </BlockContainer>
    );
}
